
import React from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { motion } from "framer-motion";

const RarityGuidePage: React.FC = () => {
  return (
    <PageContainer>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-4xl mx-auto"
      >
        <h1 className="text-3xl font-bold mb-6">Pokémon Card Rarity Guide</h1>
        <p className="text-lg text-muted-foreground mb-8">
          Understanding the rarity system is key to identifying valuable cards. This guide explains
          the different rarity levels and how to identify them.
        </p>

        <div className="grid grid-cols-1 gap-8">
          <Card>
            <CardHeader className="bg-gradient-to-r from-gray-100 to-transparent">
              <CardTitle>Basic Rarity Levels</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center pb-6 border-b">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="w-8 h-8 bg-black rounded-full"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Common</h3>
                    <p className="text-sm text-muted-foreground">
                      Identified by a black circle symbol in the bottom right corner.
                      These are the most frequently found cards in booster packs.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center pb-6 border-b">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="w-8 h-8 bg-black transform rotate-45"></div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Uncommon</h3>
                    <p className="text-sm text-muted-foreground">
                      Identified by a black diamond symbol in the bottom right corner.
                      These are less frequent than common cards but still relatively easy to find.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center pb-6 border-b">
                  <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-2xl">★</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Rare</h3>
                    <p className="text-sm text-muted-foreground">
                      Identified by a black star symbol in the bottom right corner.
                      These appear approximately once per booster pack.
                    </p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                  <div className="w-16 h-16 bg-gradient-to-br from-yellow-200 to-yellow-400 rounded-lg flex items-center justify-center">
                    <div className="text-2xl animate-pulse">★</div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Holo Rare</h3>
                    <p className="text-sm text-muted-foreground">
                      Rare cards with a holographic image. These are more valuable than standard rares
                      and have a distinctive shimmering effect on the artwork.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="bg-gradient-to-r from-blue-100 to-transparent">
              <CardTitle>Special Rarity Levels</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="h-40 bg-gradient-to-b from-purple-200 to-pink-100 rounded-lg flex items-center justify-center">
                    <div className="text-2xl font-bold text-purple-700">EX</div>
                  </div>
                  <h3 className="font-semibold text-lg">EX Cards</h3>
                  <p className="text-sm text-muted-foreground">
                    Powerful cards featuring evolved or legendary Pokémon. They have higher HP and stronger
                    attacks but give up 2 prize cards when knocked out.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="h-40 bg-gradient-to-b from-blue-200 to-cyan-100 rounded-lg flex items-center justify-center">
                    <div className="text-2xl font-bold text-blue-700">GX</div>
                  </div>
                  <h3 className="font-semibold text-lg">GX Cards</h3>
                  <p className="text-sm text-muted-foreground">
                    The successor to EX cards, featuring powerful GX attacks that can only be used once per game.
                    These often feature stunning full-art variants.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="h-40 bg-gradient-to-b from-red-200 to-orange-100 rounded-lg flex items-center justify-center">
                    <div className="text-2xl font-bold text-red-700">V</div>
                  </div>
                  <h3 className="font-semibold text-lg">V Cards</h3>
                  <p className="text-sm text-muted-foreground">
                    The current generation of powerful cards, replacing GX. They have high HP and strong
                    attacks but give up 2 prize cards when knocked out.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="h-40 bg-gradient-to-b from-emerald-200 to-green-100 rounded-lg flex items-center justify-center">
                    <div className="text-2xl font-bold text-emerald-700">VMAX</div>
                  </div>
                  <h3 className="font-semibold text-lg">VMAX Cards</h3>
                  <p className="text-sm text-muted-foreground">
                    Evolved forms of V cards with even higher HP and more powerful attacks. These cards
                    give up 3 prize cards when knocked out.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="bg-gradient-to-r from-yellow-100 to-transparent">
              <CardTitle>Special Considerations</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="space-y-6">
                <div className="p-4 border rounded-lg bg-gradient-to-r from-yellow-50 to-transparent">
                  <h3 className="font-semibold mb-2">First Edition</h3>
                  <p className="text-sm">
                    Cards from the earliest print runs often have a "1st Edition" stamp. These are significantly
                    more valuable than unlimited edition cards from the same set.
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg bg-gradient-to-r from-blue-50 to-transparent">
                  <h3 className="font-semibold mb-2">Shadowless</h3>
                  <p className="text-sm">
                    An early printing variation of Base Set cards that lacks a drop shadow around the artwork frame.
                    These are highly collectible and valuable.
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg bg-gradient-to-r from-purple-50 to-transparent">
                  <h3 className="font-semibold mb-2">Secret Rare</h3>
                  <p className="text-sm">
                    Cards with collector numbers that exceed the official set size (e.g., 165/150). These are
                    extremely rare and often feature unique artwork or card treatments.
                  </p>
                </div>
                
                <div className="p-4 border rounded-lg bg-gradient-to-r from-pink-50 to-transparent">
                  <h3 className="font-semibold mb-2">Alternate Art</h3>
                  <p className="text-sm">
                    Special variants of cards with different artwork than the standard version. These are
                    highly sought after by collectors and often command premium prices.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </motion.div>
    </PageContainer>
  );
};

export default RarityGuidePage;
